/**
 * Design Tokens
 * Visual constants for consistent styling across applications
 * Requirements: Visual Design Tokens section
 * 
 * BACKWARD COMPATIBILITY NOTE:
 * This file exports static design tokens for backward compatibility.
 * For theme-aware tokens that respond to ThemeProvider context, use:
 * - `useTheme().designTokens` - Hook for accessing theme-aware tokens in React components
 * - `mapThemeToDesignTokens(theme)` - Function for mapping ThemeTokens to DesignTokens
 * 
 * Requirements: 8.1, 8.2
 */

export const designTokens = {
  colors: {
    // Selection & Focus
    selectionBorder: '#2563eb', // Blue-600, WCAG AA compliant
    focusRing: '#3b82f6', // Blue-500
    focusRingOffset: '#ffffff',

    // Workflow States
    workflowDraft: '#f59e0b', // Amber-500
    workflowReview: '#8b5cf6', // Violet-500
    workflowApproved: '#10b981', // Emerald-500
    workflowPublished: '#06b6d4', // Cyan-500
    workflowArchived: '#6b7280', // Gray-500

    // Validation & Errors
    errorBorder: '#ef4444', // Red-500
    errorBackground: '#fef2f2', // Red-50
    errorText: '#b91c1c', // Red-700

    // Customization Markers
    overlayMarker: '#f97316', // Orange-500
    overlayMarkerBackground: '#fff7ed', // Orange-50

    // Surfaces
    canvasBackground: '#f9fafb', // Gray-50
    inspectorBackground: '#ffffff',
    blockBackground: '#ffffff',
    blockHoverBackground: '#f3f4f6', // Gray-100

    // Text
    textPrimary: '#111827', // Gray-900
    textSecondary: '#6b7280', // Gray-500
    textMuted: '#9ca3af', // Gray-400
  },

  spacing: {
    blockGap: '16px',
    blockPadding: '16px',
    inspectorWidth: '320px',
    sidebarWidth: '240px',
  },

  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: {
      xs: '12px',
      sm: '13px',
      base: '14px',
      lg: '16px',
      xl: '20px',
      '2xl': '24px',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.625',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  borders: {
    selectionWidth: '2px',
    focusRingWidth: '2px',
    focusRingOffset: '2px',
    blockRadius: '8px',
  },

  shadows: {
    blockShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    blockSelectedShadow: '0 4px 6px rgba(37, 99, 235, 0.1)',
    inspectorShadow: '-4px 0 6px rgba(0, 0, 0, 0.05)',
  },

  transitions: {
    default: '150ms ease-in-out',
    slow: '300ms ease-in-out',
  },
} as const;

export type DesignTokens = typeof designTokens;
